/* =========================================================
   Produkt-Thumbnails nach Klick sofort zentrieren
   ========================================================= */
/* =========================================================
   Aktives Produkt-Thumbnail immer zentrieren
   – Klick und Variantenwechsel
   ========================================================= */

(function initProductThumbnailCentering() {
  function centerItem(slider, item) {
    const list = slider.querySelector('.thumbnail-list');

    if (!list || !item) {
      return;
    }

    const targetLeft =
      item.offsetLeft -
      (list.clientWidth - item.offsetWidth) / 2;

    const maxLeft = Math.max(
      0,
      list.scrollWidth - list.clientWidth
    );

    list.scrollTo({
      left: Math.min(maxLeft, Math.max(0, targetLeft)),
      behavior: 'smooth'
    });
  }

  function findActiveItem(slider) {
    return (
      slider.querySelector(
        '.thumbnail-list__item .thumbnail[aria-current="true"]'
      )?.closest('.thumbnail-list__item') ||
      slider.querySelector('.thumbnail-list__item.is-active')
    );
  }

  function initialize(root = document) {
    root
      .querySelectorAll(
        'thumbnail-slider.custom-product-thumbnails'
      )
      .forEach(slider => {
        if (
          slider.dataset.customCenteringInitialized === 'true'
        ) {
          return;
        }

        slider.dataset.customCenteringInitialized = 'true';

        slider.addEventListener('click', event => {
          const item = event.target.closest(
            '.thumbnail-list__item'
          );

          if (item) {
            centerItem(slider, item);
          }
        });

        const list = slider.querySelector('.thumbnail-list');

        if (!list) {
          return;
        }

        let previousActiveItem = findActiveItem(slider);

        const observer = new MutationObserver(() => {
          const activeItem = findActiveItem(slider);

          if (!activeItem || activeItem === previousActiveItem) {
            return;
          }

          previousActiveItem = activeItem;

          requestAnimationFrame(() => {
            centerItem(slider, activeItem);
          });
        });

        observer.observe(list, {
          subtree: true,
          attributes: true,
          attributeFilter: [
            'class',
            'aria-current'
          ]
        });
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialize(),
      { once: true }
    );
  } else {
    initialize();
  }

  document.addEventListener(
    'shopify:section:load',
    event => initialize(event.target)
  );
})();


/* =========================================================
   Produkt-Medienmodal – stabiler Ein-Bild-Slider
   ========================================================= */

(function initCustomProductModalSliders() {
  const initializedModals = new WeakSet();

  function extractNumericMediaId(value) {
    const match = String(value || '').match(/(\d+)$/);
    return match ? match[1] : '';
  }

  function initializeModal(modal) {
    if (!modal || initializedModals.has(modal)) {
      return;
    }

    initializedModals.add(modal);

    let currentMediaId = '';
    let touchStartX = null;
    let touchStartY = null;
    let internalUpdate = false;
    let synchronizeTimer = null;

    function isModalOpen() {
      return (
        modal.hasAttribute('open') ||
        modal.classList.contains('active') ||
        modal.getAttribute('aria-hidden') === 'false'
      );
    }

    function getSlides() {
      const slider = modal.querySelector(
        '.custom-product-modal-slider'
      );

      if (!slider) {
        return [];
      }

      return Array.from(
        slider.querySelectorAll(
          ':scope > .custom-product-modal-slide'
        )
      ).sort((a, b) => {
        const positionA = Number(
          a.dataset.modalMediaPosition
        );
        const positionB = Number(
          b.dataset.modalMediaPosition
        );

        return positionA - positionB;
      });
    }

    function getCurrentIndex(slides) {
      if (!slides.length) {
        return -1;
      }

      if (currentMediaId) {
        const mediaIndex = slides.findIndex(slide => {
          return (
            String(slide.dataset.modalMediaId) ===
            String(currentMediaId)
          );
        });

        if (mediaIndex >= 0) {
          return mediaIndex;
        }
      }

      const activeIndex = slides.findIndex(slide => {
        return slide.classList.contains('is-active');
      });

      return activeIndex >= 0 ? activeIndex : 0;
    }

    function prepareActiveMedia(slide) {
      if (!slide) {
        return;
      }

      slide.querySelectorAll('img').forEach(image => {
        image.loading = 'eager';
        image.removeAttribute('loading');

        if (!image.getAttribute('src') && image.currentSrc) {
          image.src = image.currentSrc;
        }
      });
    }

    function pauseInactiveMedia(activeSlide) {
      getSlides().forEach(slide => {
        if (slide === activeSlide) {
          return;
        }

        slide.querySelectorAll('video').forEach(video => {
          try {
            video.pause();
          } catch (error) {
            // Komfortfunktion ohne Einfluss auf die Galerie.
          }
        });
      });
    }

    function showSlide(index) {
      const slides = getSlides();

      if (!slides.length) {
        return;
      }

      let normalizedIndex = Number(index);

      if (!Number.isFinite(normalizedIndex)) {
        normalizedIndex = 0;
      }

      if (normalizedIndex < 0) {
        normalizedIndex = slides.length - 1;
      }

      if (normalizedIndex >= slides.length) {
        normalizedIndex = 0;
      }

      const activeSlide = slides[normalizedIndex];

      if (!activeSlide) {
        return;
      }

      internalUpdate = true;

      slides.forEach(slide => {
        const active = slide === activeSlide;

        slide.hidden = false;
        slide.removeAttribute('hidden');
        slide.removeAttribute('aria-hidden-by-variant');
        slide.style.removeProperty('display');

        slide.classList.toggle('is-active', active);
        slide.setAttribute(
          'aria-hidden',
          active ? 'false' : 'true'
        );
      });

      currentMediaId = String(
        activeSlide.dataset.modalMediaId || ''
      );

      prepareActiveMedia(activeSlide);
      pauseInactiveMedia(activeSlide);

      const counter = modal.querySelector(
        '.custom-product-modal-counter'
      );

      if (counter) {
        counter.textContent =
          `${normalizedIndex + 1} / ${slides.length}`;
      }

      requestAnimationFrame(() => {
        internalUpdate = false;
      });
    }

    function showMediaById(mediaId) {
      const normalizedMediaId =
        extractNumericMediaId(mediaId);
      const slides = getSlides();

      if (!slides.length) {
        return;
      }

      const index = slides.findIndex(slide => {
        return (
          String(slide.dataset.modalMediaId) ===
          String(normalizedMediaId)
        );
      });

      showSlide(index >= 0 ? index : 0);
    }

    function showPrevious() {
      const slides = getSlides();
      const currentIndex = getCurrentIndex(slides);

      showSlide(currentIndex - 1);
    }

    function showNext() {
      const slides = getSlides();
      const currentIndex = getCurrentIndex(slides);

      showSlide(currentIndex + 1);
    }

    function getActiveMainMediaId() {
      const section = modal.closest('.shopify-section');
      const activeMainMedia = section?.querySelector(
        'media-gallery .product__media-item.is-active[data-media-id]'
      );

      return extractNumericMediaId(
        activeMainMedia?.dataset.mediaId
      );
    }

    function synchronizeModal() {
      const mediaId =
        getActiveMainMediaId() || currentMediaId;

      if (mediaId) {
        showMediaById(mediaId);
      } else {
        showSlide(0);
      }
    }

    function scheduleSynchronize() {
      if (synchronizeTimer !== null) {
        window.clearTimeout(synchronizeTimer);
      }

      requestAnimationFrame(() => {
        synchronizeModal();
      });

      synchronizeTimer = window.setTimeout(() => {
        synchronizeTimer = null;
        synchronizeModal();
      }, 80);
    }

    modal.addEventListener('click', event => {
      const previousButton = event.target.closest(
        '.custom-product-modal-prev'
      );

      if (previousButton) {
        event.preventDefault();
        event.stopPropagation();
        showPrevious();
        return;
      }

      const nextButton = event.target.closest(
        '.custom-product-modal-next'
      );

      if (nextButton) {
        event.preventDefault();
        event.stopPropagation();
        showNext();
      }
    });

    modal.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showPrevious();
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        showNext();
      }
    });

    modal.addEventListener(
      'touchstart',
      event => {
        const touch = event.touches[0];

        if (!touch) {
          return;
        }

        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
      },
      { passive: true }
    );

    modal.addEventListener(
      'touchend',
      event => {
        if (
          touchStartX === null ||
          touchStartY === null
        ) {
          return;
        }

        const touch = event.changedTouches[0];

        if (!touch) {
          return;
        }

        const deltaX = touch.clientX - touchStartX;
        const deltaY = touch.clientY - touchStartY;

        touchStartX = null;
        touchStartY = null;

        if (
          Math.abs(deltaX) < 45 ||
          Math.abs(deltaX) <= Math.abs(deltaY)
        ) {
          return;
        }

        if (deltaX < 0) {
          showNext();
        } else {
          showPrevious();
        }
      },
      { passive: true }
    );

    document.addEventListener(
      'click',
      event => {
        const gallery = event.target.closest(
          'media-gallery'
        );

        if (!gallery) {
          return;
        }

        const section = gallery.closest(
          '.shopify-section'
        );
        const sectionModal = section?.querySelector(
          `#${CSS.escape(modal.id)}`
        );

        if (sectionModal !== modal) {
          return;
        }

        const clickedMedia =
          event.target.closest(
            '.product__media-item[data-media-id]'
          ) ||
          gallery.querySelector(
            '.product__media-item.is-active[data-media-id]'
          );

        const mediaId = extractNumericMediaId(
          clickedMedia?.dataset.mediaId
        );

        if (mediaId) {
          currentMediaId = mediaId;
        }
      },
      true
    );

    document.addEventListener('change', event => {
      if (
        !event.target.closest(
          'variant-selects, variant-radios, product-form'
        )
      ) {
        return;
      }

      currentMediaId = '';

      if (isModalOpen()) {
        scheduleSynchronize();
      }
    });

    const modalObserver = new MutationObserver(
      mutations => {
        if (internalUpdate) {
          return;
        }

        const modalStateChanged = mutations.some(
          mutation =>
            mutation.type === 'attributes' &&
            mutation.target === modal
        );

        const contentChanged = mutations.some(
          mutation => mutation.type === 'childList'
        );

        if (
          isModalOpen() &&
          (modalStateChanged || contentChanged)
        ) {
          scheduleSynchronize();
        }
      }
    );

    modalObserver.observe(modal, {
      attributes: true,
      attributeFilter: [
        'open',
        'class',
        'aria-hidden'
      ],
      childList: true,
      subtree: true
    });

    modal.classList.add('custom-modal-slider-ready');
    showSlide(0);
  }

  function initializeAll(root = document) {
    if (
      root.matches?.(
        'product-modal.custom-product-media-modal'
      )
    ) {
      initializeModal(root);
    }

    root
      .querySelectorAll?.(
        'product-modal.custom-product-media-modal'
      )
      .forEach(initializeModal);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initializeAll(),
      { once: true }
    );
  } else {
    initializeAll();
  }

  document.addEventListener(
    'shopify:section:load',
    event => {
      initializeAll(event.target);
    }
  );

  const productModalObserver = new MutationObserver(
    mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (!(node instanceof Element)) {
            return;
          }

          if (
            node.matches(
              'product-modal.custom-product-media-modal'
            )
          ) {
            initializeModal(node);
          }

          node
            .querySelectorAll?.(
              'product-modal.custom-product-media-modal'
            )
            .forEach(initializeModal);
        });
      });
    }
  );

  productModalObserver.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();

/* =========================================================
   Produktmedien immer nach Shopify-Backendposition sortieren
   ========================================================= */

(function initBackendMediaSorting() {
  const sortingLocks = new WeakSet();

  function getPosition(element, attribute) {
    const position = Number(element.getAttribute(attribute));
    return Number.isFinite(position) ? position : Number.MAX_SAFE_INTEGER;
  }

  function sortChildren(container, selector, positionAttribute) {
    if (!container || sortingLocks.has(container)) {
      return;
    }

    const items = Array.from(
      container.querySelectorAll(`:scope > ${selector}`)
    );

    if (items.length < 2) {
      return;
    }

    const sortedItems = [...items].sort((a, b) => {
      return (
        getPosition(a, positionAttribute) -
        getPosition(b, positionAttribute)
      );
    });

    const orderChanged = sortedItems.some(
      (item, index) => item !== items[index]
    );

    if (!orderChanged) {
      return;
    }

    sortingLocks.add(container);

    sortedItems.forEach(item => {
      container.appendChild(item);
    });

    sortingLocks.delete(container);
  }

  function sortProductMedia(root = document) {
    root.querySelectorAll('media-gallery').forEach(gallery => {
      const viewer = gallery.querySelector(
        '[id^="GalleryViewer-"]'
      );

      const thumbnails = gallery.querySelector(
        '[id^="Slider-Thumbnails-"]'
      );

      sortChildren(
        viewer,
        '.product__media-item',
        'data-media-position'
      );

      sortChildren(
        thumbnails,
        '.thumbnail-list__item',
        'data-media-position'
      );
    });

    root
      .querySelectorAll('product-modal.custom-product-media-modal')
      .forEach(modal => {
        const modalSlider = modal.querySelector(
          '.custom-product-modal-slider'
        );

        sortChildren(
          modalSlider,
          '.custom-product-modal-slide',
          'data-modal-media-position'
        );
      });
  }

  function scheduleSorting(root = document) {
    requestAnimationFrame(() => {
      sortProductMedia(root);

      setTimeout(() => {
        sortProductMedia(root);
      }, 50);

      setTimeout(() => {
        sortProductMedia(root);
      }, 250);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => scheduleSorting(),
      { once: true }
    );
  } else {
    scheduleSorting();
  }

  document.addEventListener('change', event => {
    if (
      event.target.closest(
        'variant-selects, variant-radios, product-form'
      )
    ) {
      scheduleSorting();
    }
  });

  document.addEventListener('shopify:section:load', event => {
    scheduleSorting(event.target);
  });
})();